import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toAbsoluteUrl } from "../../../../_metronic/helpers";
import { sanitizeHtml } from "../../../helpers/sanitizeHtml";
import { AdminDiscussDetailModel } from "../../../hooks/community/models/AdminDiscussDetailModel";
import { AdminDiscussionListModel } from "../../../hooks/community/models/AdminDiscussionListModel";
import { useAdminDetails } from "../../../hooks/community/useAdminDetails";
import { LoaderOverlay } from "../../../hooks/loader/LoaderOverlay";
import { adminApproveReview, adminDeclineReview } from "../../../services/communityService";
import { defaultProfileImage } from "../../../utils/constants";
import { CommunityAdminAction } from "../enums/CommunityAdminAction";
import { CommunityAdminStatus } from "../enums/CommunityAdminStatus";
import { CommunityAdminReasonModel } from "../models/CommunityAdminReasonModel";
import { CommunityAdminActionModal } from "./CommunityAdminActionModal";

type Props = {
    detail?: AdminDiscussionListModel;
    onClose: () => void;
    onReloadList: () => void;
    onReview?: (row: AdminDiscussDetailModel) => void;
}

export const CommunityAdminDetails = ({ detail, onClose, onReloadList, onReview }: Props) => {
    const history = useHistory();
    const [action, setAction] = useState<CommunityAdminAction | null>(null);
    const [loading, setLoading] = useState(false);
   
    const { details, loading: loadDetails, error } = useAdminDetails(detail?.id);

    useEffect(() => {
        setLoading(loadDetails);
    }, [loadDetails]);

    useEffect(() => {
        if(!!error)
            onClose();
    }, [error]);

    useEffect(() => {
        if(!!details)
            window.scrollTo({
                top: 0,
                behavior: "smooth"
            });
    }, [details]);

    const _handleAction = (next: boolean, reason?: CommunityAdminReasonModel) => {
        setAction(null);
        if(!next || !reason || !detail)
            return;

        const _fetch = async () => {
            if(reason.type === 'approve') {
                try {
                    setLoading(true);
                    await adminApproveReview(detail.id);
                    onClose();
                    onReloadList();
                }
                finally {
                    setLoading(false);
                }
            }
            else {
                try {
                    setLoading(true);
                    await adminDeclineReview(detail.id, reason.reason_id, reason.note);
                    onClose();
                    onReloadList();
                }
                finally {
                    setLoading(false);
                }
            }
        }
        _fetch();
    }

    return (
        <>
        <LoaderOverlay visible={loading} message={'Loading...'} />
        { action && 
            <CommunityAdminActionModal type={action} onClose={_handleAction} />
        }
        
        { details && (
            <div className="admin-community-details-wrap">
                <div className="admin-community-details">
                    <div className="admin-community-details__info">
                        <div className="admin-community-details__header">
                            <h3>
                                {details.postType === 'circle' ? 'Mastermind Circles': 'Forum Detail'}
                            </h3>

                            <button type="button" onClick={onClose}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
                                    <path d="M15.9998 29.3332C23.3332 29.3332 29.3332 23.3332 29.3332 15.9998C29.3332 8.6665 23.3332 2.6665 15.9998 2.6665C8.6665 2.6665 2.6665 8.6665 2.6665 15.9998C2.6665 23.3332 8.6665 29.3332 15.9998 29.3332Z" stroke="#B4B4B4" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M12.2266 19.7732L19.7733 12.2266" stroke="#B4B4B4" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path d="M19.7733 19.7732L12.2266 12.2266" stroke="#B4B4B4" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </button>
                        </div>

                        {details.postType === 'circle' && (
                            <div className="admin-community-details__tools">
                                <button onClick={() => window.alert('Cooming soon')}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M8 2V5" stroke="#460107" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M16 2V5" stroke="#460107" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M3.5 9.08984H20.5" stroke="#460107" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M19.21 15.7702L15.6701 19.3102C15.5301 19.4502 15.4 19.7102 15.37 19.9002L15.18 21.2502C15.11 21.7402 15.45 22.0803 15.94 22.0103L17.29 21.8202C17.48 21.7902 17.75 21.6602 17.88 21.5202L21.4201 17.9803C22.0301 17.3703 22.3201 16.6603 21.4201 15.7603C20.5301 14.8703 19.82 15.1602 19.21 15.7702Z" stroke="#460107" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M18.7002 16.2798C19.0002 17.3598 19.8402 18.1998 20.9202 18.4998" stroke="#460107" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M12 22H8C4.5 22 3 20 3 17V8.5C3 5.5 4.5 3.5 8 3.5H16C19.5 3.5 21 5.5 21 8.5V12" stroke="#460107" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M11.9956 13.7002H12.0046" stroke="#460107" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M8.29443 13.7002H8.30341" stroke="#460107" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M8.29443 16.7002H8.30341" stroke="#460107" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                    <span>CREATE RECORD</span>
                                </button>
                            </div>
                        )}

                        <div className="admin-community-details__content">
                            <div className="admin-community-details__content__info">
                                <div className="admin-community-details__content__info__dta">
                                    <span className="dta-id">#{details.id}</span>
                                    <span className="dta-status">{CommunityAdminStatus[details.status]}</span>
                                </div>
                                <div className="admin-community-details__content__info__tags">
                                    {details.tags.map((t, i) => (
                                        <span key={i}>{t}</span>
                                    ))}
                                </div>
                                <h5>
                                    {details.title}
                                </h5>
                                <span className="admin-community-details__content__info__content" dangerouslySetInnerHTML={{ __html: sanitizeHtml(details.description) }}></span>
                            </div>
                            <svg xmlns="http://www.w3.org/2000/svg" width="528" height="1" viewBox="0 0 528 1" fill="none">
                                <path d="M0 0.5H528" stroke="#E9E9E9"/>
                            </svg>
                            <div className="admin-community-details__content__owners">
                                <div className="owner-item">
                                    <span className="owner-item__type">Owned by</span>
                                    <div className="owner-item__info">
                                        <img src={toAbsoluteUrl(details.authorAvatar) || defaultProfileImage}  onError={(e) => { e.currentTarget.src = defaultProfileImage }}/>
                                        <span className="owner-item__info__name">{details.fullName}</span>
                                        <span className="owner-item__info__tag">
                                            HOST
                                        </span>
                                    </div>
                                </div>

                                { details.facilitatorDetail && (
                                    <div className="owner-item">
                                        <span className="owner-item__type">Facilitator</span>
                                        <div className="owner-item__info">
                                            <img src={toAbsoluteUrl(details.facilitatorDetail.avatar) || defaultProfileImage} onError={(e) => { e.currentTarget.src = defaultProfileImage }}/>
                                            <span className="owner-item__info__name">{details.facilitatorDetail.fullName}</span>
                                            <span className="owner-item__info__tag">
                                                TEAM
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        { details.declineReason || details.declineNote && (
                            <div className="admin-community-details__cancel_reason">
                                { details.declineReason && 
                                    <span className="reason">{details.declineReason}</span> 
                                }
                                <span className="note">{details.declineNote}</span>
                            </div>
                        )}
                        
                        <div className="admin-community-details__actions">
                            { details.status === 'published' ? (
                                <>
                                    <button className="admin-community-details__actions_primary" style={{ marginLeft: 'auto'}} onClick={() => onReview ? onReview(details) : history.replace(`/admin/community/review/${details.id}`)}>
                                        review forum
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="4" viewBox="0 0 14 4" fill="none">
                                            <path d="M8.5 0.379395L12 3.38354H0" stroke="white"/>
                                        </svg>
                                    </button>
                                </>
                            ): details.status === 'pending' ? (
                                <>
                                    <button className="admin-community-details__actions_secundary" onClick={() => setAction(CommunityAdminAction.Decline)}>
                                        Reject
                                    </button>

                                    <button className="admin-community-details__actions_primary" onClick={() => setAction(CommunityAdminAction.Approve)}>
                                        Approve
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="4" viewBox="0 0 14 4" fill="none">
                                            <path d="M8.5 0.379395L12 3.38354H0" stroke="white"/>
                                        </svg>
                                    </button>
                                </>
                            ): (<></>)}
                        </div>
                        
                    </div>
                </div>
                <section onClick={onClose}></section>
            </div>
        )}
        </>
    )
}